import { useState } from 'react';
import { aboutInfo, siteConfig, faqData } from '@/mocks/siteData';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: `关于我们 - ${siteConfig.name}`,
            description: aboutInfo.brandIntro,
            url: `https://grcsaas.cn/about`,
            about: {
              '@type': 'Organization',
              name: siteConfig.name,
              description: aboutInfo.brandIntro,
              email: siteConfig.email,
              telephone: siteConfig.phone,
              address: {
                '@type': 'PostalAddress',
                streetAddress: siteConfig.address,
                addressLocality: '北京',
                addressCountry: 'CN',
              },
            },
          }),
        }}
      />

      <main className="pt-16 min-h-screen bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-12 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                关于我们
              </h1>
              <p className="text-sm text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                中文字幕日韩影视大全 —— 您的专属<strong>中文字幕日韩资源</strong>平台
              </p>
            </div>

            <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/70 mb-10">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">品牌介绍</h2>
              <p className="text-sm text-foreground-700 leading-relaxed">{aboutInfo.brandIntro}</p>
              <div className="mt-6 bg-primary-50 rounded-xl p-5 border border-primary-100">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 flex-shrink-0 mt-0.5">
                    <i className="ri-quote-text text-white text-sm"></i>
                  </div>
                  <p className="text-sm text-primary-800 leading-relaxed font-medium">
                    "我们的使命：{aboutInfo.mission}"
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              {aboutInfo.features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-xl p-5 border border-background-200/70"
                >
                  <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="bg-accent-50 rounded-2xl p-8 border border-accent-100 mb-10">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6 text-center">平台口碑</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                {aboutInfo.stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="font-heading text-3xl md:text-4xl font-bold text-primary-600 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs text-foreground-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/70 mb-10">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">联系我们</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <a
                  href={`mailto:${siteConfig.email}`}
                  className="flex flex-col items-center p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <span className="text-xs text-foreground-500 mb-1">电子邮箱</span>
                  <span className="text-sm font-medium text-foreground-800">{siteConfig.email}</span>
                </a>

                <div className="flex flex-col items-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <span className="text-xs text-foreground-500 mb-1">客服电话</span>
                  <span className="text-sm font-medium text-foreground-800">{siteConfig.phone}</span>
                </div>

                <div className="flex flex-col items-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <span className="text-xs text-foreground-500 mb-1">公司地址</span>
                  <span className="text-sm font-medium text-foreground-800 text-center">{siteConfig.address}</span>
                </div>
              </div>
            </div>

            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6 text-center">常见问题</h2>
              <div className="space-y-3">
                {faqData.slice(0, 4).map((faq, index) => (
                  <div
                    key={index}
                    className="bg-background-100 rounded-xl border border-background-200/70 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="font-heading font-semibold text-sm text-foreground-800 pr-4">
                        {faq.question}
                      </span>
                      <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full transition-colors ${
                        openFaqIndex === index ? 'bg-primary-500 text-white' : 'bg-background-200 text-foreground-500'
                      }`}>
                        <i className={`text-sm ${openFaqIndex === index ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                      </div>
                    </button>
                    <div
                      className={`transition-all duration-300 overflow-hidden ${
                        openFaqIndex === index ? 'max-h-96 pb-4 px-5' : 'max-h-0'
                      }`}
                    >
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}